import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';

/**
 * Admin panel: system status overview and health checks.
 */
export default function AdminOverview({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/system-status`, {
          headers: getAuthHeaders(),
        });
        if (r.ok) {
          setStatus(await r.json());
        } else {
          setError(t('admin.statusFetchFailed'));
        }
      } catch {
        setError(t('admin.statusUnavailable'));
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [getAuthHeaders, t]);

  const containers = status?.docker?.containers || [];
  const isContainerRunning = (c) => typeof c.status === 'string' && c.status.toLowerCase().startsWith('up');

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navOverview')}</h1>

      {loading ? (
        <div className="admin-info">{t('live.loading')}</div>
      ) : error ? (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.systemStatus')}</div>
          <div className="admin-info">{error}</div>
        </div>
      ) : status ? (
        <>
          <div className="admin-stat-grid">
            {status.disk?.percent !== undefined && (
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.disk')}</div>
                <div className="admin-stat-value">{status.disk.percent}%</div>
                <div className="admin-info" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {status.disk.used_gb}/{status.disk.total_gb} GB
                </div>
              </div>
            )}
            {status.postgresql?.matches !== undefined && (
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.matchesDb')}</div>
                <div className="admin-stat-value">{status.postgresql.matches}</div>
              </div>
            )}
            {status.mariadb?.matches !== undefined && (
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.matchesMatchzy')}</div>
                <div className="admin-stat-value">{status.mariadb.matches}</div>
              </div>
            )}
            {status.postgresql?.elo_records !== undefined && (
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.eloRecords')}</div>
                <div className="admin-stat-value">{status.postgresql.elo_records}</div>
              </div>
            )}
            {status.redis?.used_memory_human && (
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.redisMemory')}</div>
                <div className="admin-stat-value">{status.redis.used_memory_human}</div>
                <div className="admin-info" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {t('admin.clients', { count: status.redis.connected_clients })}
                </div>
              </div>
            )}
          </div>

          {containers.length > 0 && (
            <div className="admin-card">
              <div className="admin-card-title">{t('admin.dockerServices')}</div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.colService')}</th>
                    <th>{t('admin.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {containers.map((c) => {
                    const running = isContainerRunning(c);
                    return (
                      <tr key={c.name}>
                        <td>{c.name}</td>
                        <td style={{ color: running ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                          {c.status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
